import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API } from 'aws-amplify';
import { AppDispatch, RootState } from '../store';
import { buildReducers, createLazyList, createRequestStatusObject, RequestStatusObject } from '../utils';
import { LazyList, LazyListRequest, LazyListResponse } from '../../types/types';
import {
	ApproveVerificationRequest,
	CreateVerificationRequest,
	ForwardVerificationRequest,
	RequestMoreInfoVerificationRequest,
	AttachAdditionalVerificationFilesRequest,
	Verification,
	VerificationOrganisation,
	VerificationStatus,
	VerificationUser
} from '../../types/verification';
import { readUser } from './user';
import { readDocument } from './document';
import { enqueueSnackbar } from 'notistack';
import i18n from '../../i18n';

const initialState: {
	currentVerification: Verification | undefined;
	requestVerificationForm: Partial<CreateVerificationRequest>; // used fo the form
	userList: LazyList<VerificationUser>;
	organisationList: LazyList<VerificationOrganisation>;
	readVerificationRequest: RequestStatusObject;
	listVerificationsRequest: RequestStatusObject;
	createVerificationRequest: RequestStatusObject;
	cancelVerificationRequest: RequestStatusObject;
	updateVerificationRequest: RequestStatusObject;
	isLoading?: boolean;
} = {
	currentVerification: undefined,
	requestVerificationForm: {},
	userList: createLazyList(),
	organisationList: createLazyList(),
	readVerificationRequest: createRequestStatusObject(),
	listVerificationsRequest: createRequestStatusObject(),
	createVerificationRequest: createRequestStatusObject(),
	cancelVerificationRequest: createRequestStatusObject(),
	updateVerificationRequest: createRequestStatusObject(),
	isLoading: false
};
export const readVerification = createAsyncThunk<Verification, string, { dispatch: AppDispatch; state: RootState }>(
	'verification/readCurrent',

	async (id: string, { dispatch }) => {
		// current user Id

		const result = await API.get('inspirusapi', `/verification-requests/${id}`, {
			headers: {},
			response: true,
			queryStringParameters: {}
		});

		const verification: Verification = result.data[0];

		if (verification.userId) {
			const user = await dispatch(readUser({ id: verification.userId })).unwrap();
			verification.user = user;
		}
		if (verification.documentId) {
			const document = await dispatch(readDocument(verification.documentId)).unwrap();
			verification.document = document;
		}
		return verification;
	}
);

export const listUserVerifications = createAsyncThunk<
	LazyList<Verification>,
	{ userId?: string } | undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/list',

	async (params) => {
		const queryStringParameters: any = {};
		if (params?.userId) {
			queryStringParameters.userId = params.userId;
		}
		const result = await API.get('inspirusapi', '/verification-requests', {
			headers: {},
			response: true,
			queryStringParameters
		});
		const list = {
			totalCount: result.data.items.length,
			items: result.data.items
		};
		return list;
	}
);

export const listOrganisationVerifications = createAsyncThunk<
	LazyListResponse<Verification>,
	{ organisationId: string; status: VerificationStatus } & LazyListRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/listOrganisation',

	async ({ organisationId, status, limit, next }) => {
		// current user Id
		const result = await API.get('inspirusapi', `/organisations/${organisationId}/verification-requests`, {
			headers: {},
			response: true,
			queryStringParameters: {
				status,
				limit,
				next
			}
		});

		return { ...result.data };
	}
);

export const createVerification = createAsyncThunk<
	Verification,
	undefined,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/create',

	async (_, { getState }) => {
		// current user Id
		const verification = getState().verification.requestVerificationForm;

		const result = await API.post('inspirusapi', '/verification-requests/', {
			headers: {},
			response: true,
			body: verification
		});

		return result.data;
	}
);

export const cancelVerification = createAsyncThunk<
	any,
	{ verificationId: string; documentId?: string },
	{ dispatch: AppDispatch; state: RootState }
>('verification/cancel', async ({ verificationId, documentId }) => {
	if (!verificationId) {
		throw new Error('verificationId is required');
	}

	const body = { verificationId, documentId };

	await API.del('inspirusapi', '/verification-requests', {
		headers: {},
		response: true,
		body
	});

	return verificationId;
});

export const approveVerification = createAsyncThunk<
	Verification,
	ApproveVerificationRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/approve',

	async ({ id, notes, document }) => {
		const result = await API.put('inspirusapi', `/verification-requests/${id}`, {
			headers: {},
			response: true,
			body: {
				notes,
				document,
				status: VerificationStatus.APPROVED
			}
		});

		return result.data.updatedAttributes;
	}
);

export const rejectVerification = createAsyncThunk<
	Verification,
	ApproveVerificationRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/reject',

	async ({ id, notes }) => {
		const result = await API.put('inspirusapi', `/verification-requests/${id}`, {
			headers: {},
			response: true,
			body: {
				notes,
				status: VerificationStatus.DECLINED
			}
		});

		return result.data.updatedAttributes;
	}
);

export const forwardVerification = createAsyncThunk<
	Verification,
	ForwardVerificationRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/forward',

	async ({ id, organisationId }) => {
		const result = await API.put('inspirusapi', `/verification-requests/${id}`, {
			headers: {},
			response: true,
			body: {
				organisationId,
				status: VerificationStatus.PENDING
			}
		});

		return result.data.updatedAttributes;
	}
);

export const requestMoreInfoVerification = createAsyncThunk<
	Verification,
	RequestMoreInfoVerificationRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/requestMoreInfo',

	async ({ id, message }) => {
		const result = await API.put('inspirusapi', `/verification-requests/${id}`, {
			headers: {},
			response: true,
			body: {
				status:
					message?.senderName === 'organization'
						? VerificationStatus.REQUEST_MORE_INFO
						: VerificationStatus.PENDING,
				message
			}
		});

		return result.data.updatedAttributes;
	}
);

export const attachAdditionalVerificationFiles = createAsyncThunk<
	Verification,
	AttachAdditionalVerificationFilesRequest,
	{ dispatch: AppDispatch; state: RootState }
>(
	'verification/attachAdditionalVerificationFiles',

	async ({ id, fileKeys }) => {
		const result = await API.put('inspirusapi', `/verification-requests/${id}`, {
			headers: {},
			response: true,
			body: {
				fileKeys
			}
		});

		return result.data.updatedAttributes;
	}
);

const verificationSlice = createSlice({
	name: 'verification',
	initialState,
	reducers: {
		resetVerification: (state) => {
			state = initialState;
			return state;
		},
		setCurrentVerificationRequest: (state, action) => {
			const verification = action.payload as Partial<CreateVerificationRequest>;
			state.requestVerificationForm = {
				...state.requestVerificationForm,
				...verification
			};
		}
	},
	extraReducers: (builder) => {
		buildReducers(builder, readVerification, 'readVerificationRequest', {
			onFulfilled: (state, payload) => {
				state.isLoading = false;
				state.currentVerification = payload;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar(
					i18n.t('common.types.organisation.metrics.verificationsRequests.readVerificationFail'),
					{ variant: 'error' }
				);
				state.isLoading = false;
			}
		});
		buildReducers(builder, listUserVerifications, 'listVerificationsRequest', {
			onFulfilled: (state, payload) => {
				state.isLoading = false;
				state.userList = payload;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				state.isLoading = false;
			}
		});
		buildReducers(builder, listOrganisationVerifications, 'listVerificationsRequest', {
			onFulfilled: (state, payload) => {
				const list = state.organisationList;
				list.items = payload.items;
				list.next = payload.next;
				list.totalCount = payload.totalCount;
				state.isLoading = false;
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar('failed to list verification requests', { variant: 'error' });
				state.isLoading = false;
			}
		});

		buildReducers(builder, createVerification, 'createVerificationRequest', {
			onFulfilled: (state) => {
				enqueueSnackbar(i18n.t('pages.verifications.request.successMessage'), { variant: 'success' });
				state.requestVerificationForm = {};
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.verifications.request.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, cancelVerification, 'cancelVerificationRequest', {
			onFulfilled: (state, verificationId) => {
				if (verificationId) {
					const item = state.userList.items.find((item: any) => item.id === verificationId);

					if (item) {
						item.status = VerificationStatus.CANCELLED;
					}
				}
				state.isLoading = false;
				enqueueSnackbar('cancelled verification request successfully', { variant: 'success' });
				state.requestVerificationForm = {};
			},
			onPending: (state) => {
				state.isLoading = true;
			},
			onRejected: (state) => {
				enqueueSnackbar('failed to cancel verification request', { variant: 'error' });
				state.isLoading = false;
			}
		});
		buildReducers(builder, approveVerification, 'updateVerificationRequest', {
			onFulfilled: (state, payload) => {
				enqueueSnackbar(i18n.t('pages.verifications.approve.successMessage'), { variant: 'success' });
				state.currentVerificationRequest = { ...state.currentVerificationRequest, payload };
				const blockchainIdOrTx = payload.txId || payload.blockchainId;
				if (blockchainIdOrTx) {
					enqueueSnackbar(
						i18n.t('pages.verifications.approve-blockchain.successMessage') + blockchainIdOrTx,
						{ variant: 'success' }
					);
				}
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.verifications.approve.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, rejectVerification, 'updateVerificationRequest', {
			onFulfilled: (state, payload) => {
				enqueueSnackbar(i18n.t('pages.verifications.reject.successMessage'), { variant: 'success' });
				state.currentVerificationRequest = { ...state.currentVerificationRequest, payload };
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.verifications.reject.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, forwardVerification, 'updateVerificationRequest', {
			onFulfilled: (state, payload) => {
				enqueueSnackbar(i18n.t('pages.verifications.forward.successMessage'), { variant: 'success' });
				state.currentVerificationRequest = { ...state.currentVerificationRequest, payload };
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.verifications.forward.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, requestMoreInfoVerification, 'updateVerificationRequest', {
			onFulfilled: (state, payload) => {
				enqueueSnackbar(i18n.t('pages.verifications.forward.successMessage'), { variant: 'success' });
				state.currentVerificationRequest = { ...state.currentVerificationRequest, payload };
				const item = state.userList.items.find((item: any) => item.id === payload.id);

				if (item) {
					item.status = payload.status;
				}
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.verifications.forward.errorMessage'), { variant: 'error' });
			}
		});
		buildReducers(builder, attachAdditionalVerificationFiles, 'updateVerificationRequest', {
			onFulfilled: (state, payload) => {
				enqueueSnackbar(i18n.t('pages.verifications.forward.successMessage'), { variant: 'success' });
				state.currentVerificationRequest = { ...state.currentVerificationRequest, payload };
				const item = state.userList.items.find((item: any) => item.id === payload.id);

				if (item) {
					item.fileKeys = payload.fileKeys;
				}
			},
			onRejected: () => {
				enqueueSnackbar(i18n.t('pages.verifications.forward.errorMessage'), { variant: 'error' });
			}
		});
	}
});

export const { resetVerification, setCurrentVerificationRequest } = verificationSlice.actions;
export default verificationSlice.reducer;
